import { keepPreviousData, queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { unwrap, type ApiClient } from '../client';
import type { components } from '../schema.gen';
import { queryKeys } from './keys';

export type PayCheck = components['schemas']['PayCheck'];
export type PayHistoryDisplay = components['schemas']['PayHistoryDisplay'];
export type CategoryBreakdown = components['schemas']['CategoryBreakdown'];
export type BreakdownScope = components['schemas']['BreakdownScope'];
export type CheckDetail = components['schemas']['CheckDetail'];
export type PendingPayDetail = components['schemas']['PendingPayDetail'];

/** A rep's pending pay for the upcoming pay date. Searched in the browser, so no `q`. */
export function pendingQuery(api: ApiClient, repId?: string) {
  return queryOptions({
    queryKey: queryKeys.pending(repId),
    queryFn: () =>
      unwrap(api.GET('/api/pay/pending', { params: { query: repId ? { repId } : {} } })),
  });
}

export function usePending(repId?: string) {
  return useQuery(pendingQuery(useApiClient(), repId));
}

/**
 * A rep's checks, newest first: the caller's own, or a supervised rep's (`repId`). With
 * them, `display`: which parts of the pay history page the tenant shows (A15).
 */
export function checksQuery(api: ApiClient, repId?: string) {
  return queryOptions({
    queryKey: queryKeys.checks(repId),
    queryFn: () =>
      unwrap(api.GET('/api/pay/checks', { params: { query: repId ? { repId } : {} } })),
  });
}

export function useChecks(repId?: string, enabled = true) {
  return useQuery({ ...checksQuery(useApiClient(), repId), enabled });
}

/** One check: a rep's line items for a pay date. Searched in the browser, so no `q`. */
export function checkQuery(api: ApiClient, payDate: string, repId?: string) {
  return queryOptions({
    queryKey: queryKeys.check(payDate, repId),
    queryFn: () =>
      unwrap(
        api.GET('/api/pay/checks/{pay_date}', {
          params: { path: { pay_date: payDate }, query: repId ? { repId } : {} },
        }),
      ),
  });
}

export function useCheck(payDate: string, repId?: string) {
  return useQuery(checkQuery(useApiClient(), payDate, repId));
}

export interface BreakdownParams {
  repId?: string | undefined;
  scope: BreakdownScope;
  /** Range only, both ISO dates. */
  from?: string | undefined;
  to?: string | undefined;
}

/** The category breakdown for a scope. A range without both dates isn't fetched. */
export function categoriesQuery(api: ApiClient, { repId, scope, from, to }: BreakdownParams) {
  const range = scope === 'range' ? { from, to } : {};
  return queryOptions({
    queryKey: queryKeys.categories(repId, scope, range.from, range.to),
    queryFn: () =>
      unwrap(
        api.GET('/api/pay/categories', {
          params: {
            query: {
              scope,
              ...(repId ? { repId } : {}),
              ...(range.from ? { from: range.from } : {}),
              ...(range.to ? { to: range.to } : {}),
            },
          },
        }),
      ),
    enabled: scope !== 'range' || Boolean(from && to && from <= to),
  });
}

/** Keeps the last scope's breakdown on screen while the next one loads. */
export function useCategories(params: BreakdownParams) {
  return useQuery({
    ...categoriesQuery(useApiClient(), params),
    placeholderData: keepPreviousData,
  });
}

/** A check's PDF statement (pc-o19.4), with the file name the API gives it. */
export function useDownloadStatement() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async ({
      payDate,
      repId,
    }: {
      payDate: string;
      repId?: string | undefined;
    }): Promise<{ blob: Blob; fileName: string }> => {
      const result = await api.GET('/api/pay/checks/{pay_date}/statement.pdf', {
        params: { path: { pay_date: payDate }, query: repId ? { repId } : {} },
        parseAs: 'blob',
      });
      const blob = await unwrap(Promise.resolve(result));
      const disposition = result.response.headers.get('Content-Disposition') ?? '';
      const name = /filename="([^"]+)"/.exec(disposition);
      return { blob, fileName: name?.[1] ?? `statement-${payDate}.pdf` };
    },
  });
}
